/*
 *                     NOTICE
 *            COPYRIGHT 2019 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { AbActionBarMenuRCmp } from "abreact/incubator/ui/component/actionBar/AbActionBarMenuRCmp";
import { AbActionMenuSubmenu } from "abreact/incubator/ui/component/actionBar/AbActionMenuSubmenu";
import { ActionBarMenuMachine } from "abreact/incubator/ui/component/actionBar/ActionBarMenuMachine";
import { AbWebServices } from "abweb/service/AbWebServices";
import { Assert } from "abweb/utils/Assert";
import { ClassUtils } from "abweb/utils/ClassUtils";
import { ElementUtils } from "abweb/utils/ElementUtils";
import { when } from "mobx";
import * as React from "react";
import * as ReactDOM from "react-dom";
export class ActionMenuUtils {
  /**
   * Given a KeyboardEvent, look up the items from the ActionMenu on the holder. If one is found with a keyboard
   * shortcut that matches the event, call its action and return true. Otherwise, return false.
   *
   * <p>If more than one item matches the keyboard shortcut, this will throw an <code>IllegalStateError</code></p>
   *
   * @param e         the <code>KeyboardEvent<code> to process
   * @param holder    the <code>{@link IActionMenuHolder}</code> that will be used to process the event
   * @returns {Promise<boolean>}  <code>true if the <code>holder</code> processed the event, <code>false</code> if not.
   */
  static async onKeyPressPerformAction(e, holder) {
    const handlers = await ActionMenuUtils.getShortcutHandlers(holder);
    return AbWebServices.keyboardService.onKeyPressHandleShortcuts(e, handlers, holder.keyboardShortcutGroupLabel);
  }
  static async getShortcutHandlers(holder) {
    // Get the items. They might not be flat, because there can be submenus
    const actionObject = await holder.getActionTarget();
    if (actionObject === null) {
      return [];
    }
    const itemsAndMachines = await holder.getActionMenuItems(actionObject, true);

    // Flatten them into a single array
    const items = [];
    this.flatten(items, itemsAndMachines);
    return items;
  }

  /**
   * When the mouse is clicked, show the menu on the holder
   *
   * @param m               The <code>MouseEvent</code> to process
   * @param holder          The <code>{@link IActionMenuHolder}</code> that will be used to process the event
   * @param actionObject    The object to act on. If not provided, the <code>holder's</code>
   *                        <code>{@link IActionMenuHolder.getActionTarget() getActionTarget()}</code> will be called
   * @returns {Promise<void>}
   */
  static async onClickShowMenu(m, holder, actionObject) {
    if (m.button === 0) {
      m = m;
      await ActionMenuUtils.showMenu(holder, m, actionObject);
    }
    return;
  }

  /**
   * When the mouse is right-clicked, show the menu on the holder
   *
   * @param m               The <code>MouseEvent</code> to process
   * @param holder          The <code>{@link IActionMenuHolder}</code> that will be used to process the event
   * @param actionObject    The object to act on. If not provided, the <code>holder's</code>
   *                        <code>{@link IActionMenuHolder.getActionTarget() getActionTarget()}</code> will be called
   * @returns {Promise<boolean>}
   */
  static async onRightClickShowMenu(m, holder, actionObject) {
    if (m.button === 2) {
      m = m;
      await ActionMenuUtils.showMenu(holder, m, actionObject);
    }
    return;
  }

  /**
   * M1A1 array flattening function
   *
   * @param flatItems
   * @param items
   */
  static flatten(flatItems, items) {
    items.forEach(item => {
      if (ClassUtils.isInstanceOf(item, AbActionMenuSubmenu)) {
        this.flatten(flatItems, item.actions);
      } else {
        flatItems.push(item);
      }
    });
  }

  /**
   * Shows the action menu for the holder at the specified coordinates and waits for it to close.
   *
   * @param holder          The <code>{@link IActionMenuHolder}</code> that will be used to process the event
   * @param e               The <code>MouseEvent</code> to process
   * @param actionObject    The object to act on. If not provided, the <code>holder's</code>
   *                        <code>{@link IActionMenuHolder.getActionTarget() getActionTarget()}</code> will be called
   * @return {Promise<void>}
   */
  static async showMenu(holder, e, actionObject) {
    e.preventDefault();
    e.stopPropagation();
    const menuX = e.clientX + 10;
    const menuY = e.clientY + 10;
    let machine;
    machine = new ActionBarMenuMachine(null, [], (item, x, y) => {
      ActionMenuUtils.performAction(machine, item, x, y);
    });
    const parentNode = e.target;
    const menu = /*#__PURE__*/React.createElement(AbActionBarMenuRCmp, {
      machine: machine
    });
    const div = parentNode.ownerDocument.createElement("div");
    ElementUtils.setCssDisplay(div, false);
    ReactDOM.render(menu, div);
    parentNode.appendChild(div);
    if (actionObject === undefined) {
      actionObject = await holder.getActionTarget();
    }
    const items = await holder.getActionMenuItems(actionObject);
    machine.menuItems = items;
    machine.open(menuX, menuY);
    await when(() => !machine.isOpen);
    div.remove();
  }
  static performAction(machine, item, x, y) {
    Assert.assert(!ClassUtils.isInstanceOf(item, AbActionMenuSubmenu), "Found unexpected AbActionMenuSubmenu");

    // If it's a submenu, toggle its state
    if (ClassUtils.isInstanceOf(item, ActionBarMenuMachine)) {
      if (item.isOpen) {
        item.close();
      } else {
        item.open(x, y);
      }
    } else {
      // Call the item's code and hide everything
      let menuBarItem = item;
      if (!menuBarItem.enabled) {
        return;
      }
      if (menuBarItem.callback) {
        try {
          menuBarItem.callback();
        } finally {
          machine.close();
        }
      }
    }
  }
  static getTooltip(menuItem) {
    const label = menuItem.label ? menuItem.label : "";
    const shortcut = menuItem.shortcut ? `(${menuItem.shortcut.toString()})` : "";
    const labelShortcutSep = menuItem.label && menuItem.shortcut ? " " : "";
    const labelAndShortcut = `${label}${labelShortcutSep}${shortcut}`;
    if (menuItem.enabled) {
      return labelAndShortcut;
    }
    const disabledReasons = menuItem.disabledReasons;
    if (disabledReasons == null || disabledReasons.length === 0) {
      return labelAndShortcut;
    }
    return `${labelAndShortcut}\n${disabledReasons.join("\n")}`;
  }
}
//# sourceMappingURL=ActionMenuUtils.js.map
